<template>
  <div class="paged-table">
    <el-table
      ref="table"
      class="admin-data-table"
      :data="rows"
      :height="tableHeight"
      border
      stripe
      v-loading="loading"
    >
      <slot />
    </el-table>
    <div
      ref="horizontalTrack"
      class="persistent-table-scrollbar"
      :class="{ 'is-scrollable': horizontalScrollable }"
      role="scrollbar"
      aria-orientation="horizontal"
      :aria-valuemin="0"
      :aria-valuemax="maxHorizontalScroll"
      :aria-valuenow="horizontalScrollLeft"
      @mousedown="handleTrackMouseDown"
      @wheel.prevent="handleScrollbarWheel"
    >
      <div
        class="persistent-table-scrollbar__thumb"
        :style="horizontalThumbStyle"
        @mousedown.stop.prevent="startThumbDrag"
      />
    </div>
    <div class="pager-row">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        @current-change="$emit('page-change', $event)"
        @size-change="$emit('size-change', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "PagedTable",
  data() {
    return {
      tableHeight: null,
      resizeFrame: null,
      resizeObserver: null,
      mainElement: null,
      pageRoot: null,
      tableBody: null,
      scrollbarFrame: null,
      scrollbarObserver: null,
      horizontalScrollLeft: 0,
      horizontalScrollWidth: 0,
      horizontalClientWidth: 0,
      horizontalTrackWidth: 0,
      dragStartX: 0,
      dragStartScrollLeft: 0
    };
  },
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 20 },
    total: { type: Number, default: 0 }
  },
  computed: {
    horizontalScrollable() {
      return this.horizontalScrollWidth > this.horizontalClientWidth + 1;
    },
    maxHorizontalScroll() {
      return Math.max(0, this.horizontalScrollWidth - this.horizontalClientWidth);
    },
    horizontalThumbWidth() {
      const availableWidth = Math.max(0, this.horizontalTrackWidth - 6);
      if (!availableWidth || !this.horizontalScrollable) return availableWidth;
      return Math.max(48, Math.round(availableWidth * this.horizontalClientWidth / this.horizontalScrollWidth));
    },
    horizontalThumbOffset() {
      const travel = Math.max(0, this.horizontalTrackWidth - 6 - this.horizontalThumbWidth);
      if (!travel || !this.maxHorizontalScroll) return 3;
      return 3 + travel * this.horizontalScrollLeft / this.maxHorizontalScroll;
    },
    horizontalThumbStyle() {
      return {
        width: `${this.horizontalThumbWidth}px`,
        transform: `translateX(${this.horizontalThumbOffset}px)`
      };
    }
  },
  mounted() {
    this.mainElement = this.$el.closest(".admin-main");
    if (this.mainElement) {
      this.mainElement.classList.add("has-paged-table");
      this.mainElement.scrollTop = 0;
      this.pageRoot = this.$el;
      while (this.pageRoot.parentElement && this.pageRoot.parentElement !== this.mainElement) {
        this.pageRoot = this.pageRoot.parentElement;
      }
    }

    window.addEventListener("resize", this.scheduleTableResize);
    if (window.ResizeObserver && this.pageRoot) {
      this.resizeObserver = new ResizeObserver(this.scheduleTableResize);
      this.resizeObserver.observe(this.pageRoot);
    }
    this.$nextTick(() => {
      this.bindTableBody();
      this.scheduleTableResize();
      this.scheduleScrollbarMeasure();
    });
  },
  updated() {
    this.$nextTick(this.scheduleScrollbarMeasure);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.scheduleTableResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
    if (this.scrollbarObserver) this.scrollbarObserver.disconnect();
    if (this.scrollbarFrame) window.cancelAnimationFrame(this.scrollbarFrame);
    if (this.tableBody) this.tableBody.removeEventListener("scroll", this.handleTableScroll);
    document.removeEventListener("mousemove", this.handleThumbDrag);
    document.removeEventListener("mouseup", this.stopThumbDrag);
    if (this.mainElement) this.mainElement.classList.remove("has-paged-table");
  },
  methods: {
    bindTableBody() {
      const table = this.$refs.table;
      const nextTableBody = table && table.$el && table.$el.querySelector(".el-table__body-wrapper");
      if (!nextTableBody || nextTableBody === this.tableBody) return;
      if (this.tableBody) this.tableBody.removeEventListener("scroll", this.handleTableScroll);
      if (this.scrollbarObserver) this.scrollbarObserver.disconnect();
      this.tableBody = nextTableBody;
      this.tableBody.addEventListener("scroll", this.handleTableScroll, { passive: true });

      if (window.ResizeObserver) {
        this.scrollbarObserver = new ResizeObserver(this.scheduleScrollbarMeasure);
        this.scrollbarObserver.observe(this.tableBody);
        if (this.$refs.horizontalTrack) this.scrollbarObserver.observe(this.$refs.horizontalTrack);
      }
    },
    scheduleScrollbarMeasure() {
      if (this.scrollbarFrame) window.cancelAnimationFrame(this.scrollbarFrame);
      this.scrollbarFrame = window.requestAnimationFrame(() => {
        this.scrollbarFrame = null;
        this.measureHorizontalScrollbar();
      });
    },
    measureHorizontalScrollbar() {
      this.bindTableBody();
      const track = this.$refs.horizontalTrack;
      if (!this.tableBody || !track) return;
      this.horizontalScrollLeft = Math.round(this.tableBody.scrollLeft);
      this.horizontalScrollWidth = this.tableBody.scrollWidth;
      this.horizontalClientWidth = this.tableBody.clientWidth;
      this.horizontalTrackWidth = track.clientWidth;
    },
    handleTableScroll() {
      if (!this.tableBody) return;
      this.horizontalScrollLeft = Math.round(this.tableBody.scrollLeft);
    },
    setHorizontalScroll(value) {
      if (!this.tableBody) return;
      this.tableBody.scrollLeft = Math.max(0, Math.min(this.maxHorizontalScroll, value));
      this.handleTableScroll();
    },
    handleTrackMouseDown(event) {
      if (!this.horizontalScrollable) return;
      const track = this.$refs.horizontalTrack;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const travel = Math.max(0, this.horizontalTrackWidth - 6 - this.horizontalThumbWidth);
      if (!travel) return;
      const targetOffset = event.clientX - rect.left - 3 - this.horizontalThumbWidth / 2;
      this.setHorizontalScroll(targetOffset / travel * this.maxHorizontalScroll);
    },
    handleScrollbarWheel(event) {
      if (!this.horizontalScrollable) return;
      this.setHorizontalScroll(this.horizontalScrollLeft + (event.deltaX || event.deltaY));
    },
    startThumbDrag(event) {
      if (!this.horizontalScrollable) return;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.horizontalScrollLeft;
      document.addEventListener("mousemove", this.handleThumbDrag);
      document.addEventListener("mouseup", this.stopThumbDrag);
    },
    handleThumbDrag(event) {
      const travel = Math.max(0, this.horizontalTrackWidth - 6 - this.horizontalThumbWidth);
      if (!travel) return;
      const delta = (event.clientX - this.dragStartX) * this.maxHorizontalScroll / travel;
      this.setHorizontalScroll(this.dragStartScrollLeft + delta);
    },
    stopThumbDrag() {
      document.removeEventListener("mousemove", this.handleThumbDrag);
      document.removeEventListener("mouseup", this.stopThumbDrag);
    },
    scheduleTableResize() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateTableHeight();
      });
    },
    updateTableHeight() {
      const table = this.$refs.table;
      const tableElement = table && table.$el;
      if (!tableElement || !this.mainElement || !this.pageRoot) return;

      if (this.mainElement.scrollTop) this.mainElement.scrollTop = 0;
      const mainStyle = window.getComputedStyle(this.mainElement);
      const mainBottom = this.mainElement.getBoundingClientRect().bottom - (parseFloat(mainStyle.paddingBottom) || 0);
      const pageBottom = this.pageRoot.getBoundingClientRect().bottom;
      const currentHeight = tableElement.getBoundingClientRect().height;
      const nextHeight = Math.max(180, Math.floor(currentHeight + mainBottom - pageBottom));

      if (Math.abs(Number(this.tableHeight || currentHeight) - nextHeight) <= 1) return;
      this.tableHeight = nextHeight;
      this.$nextTick(() => {
        if (this.$refs.table) this.$refs.table.doLayout();
        this.scheduleScrollbarMeasure();
      });
    }
  }
};
</script>
